<template>
    <div class="app-hub-page customer-data-page">
        <h1 class="app-hub-page-title">线索中心</h1>
        <el-card class="app-hub-card app-hub-card--body-pad" shadow="never">
            <div slot="header" class="customer-data-page__header app-hub-card-header">
                <div class="customer-data-page__header-leading">
                    <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增线索</el-button>
                </div>
                <div class="customer-data-page__toolbar">
                    <el-select
                        v-model="channelFilter"
                        size="small"
                        clearable
                        filterable
                        placeholder="渠道查询"
                        class="customer-data-page__channel-filter"
                        @change="onChannelFilterChange"
                    >
                        <el-option
                            v-for="opt in leadCreatorOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                        />
                    </el-select>
                    <el-select
                        v-model="stageFilter"
                        size="small"
                        clearable
                        placeholder="跟进阶段"
                        class="customer-data-page__stage-filter"
                        @change="onLeadStageFilterChange"
                    >
                        <el-option
                            v-for="opt in stageOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                        />
                    </el-select>
                    <el-input
                        v-model.trim="keyword"
                        size="small"
                        clearable
                        placeholder="公司名称、信用机构代码证搜索"
                        class="customer-data-page__search app-search-input"
                        @keyup.enter.native="searchLeadsFromToolbar"
                    >
                        <el-button slot="append" icon="el-icon-search" @click="searchLeadsFromToolbar" />
                    </el-input>
                    <el-button size="small" @click="resetLeadToolbarFilters">重置</el-button>
                </div>
            </div>

            <el-row :gutter="12" class="app-hub-stats">
                <el-col :xs="12" :sm="12" :md="6">
                    <div class="app-hub-stat">
                        <div class="app-hub-stat__label">线索总数</div>
                        <div class="app-hub-stat__value">{{ summary.total }}</div>
                    </div>
                </el-col>
                <el-col :xs="12" :sm="12" :md="6">
                    <div class="app-hub-stat">
                        <div class="app-hub-stat__label">跟进中</div>
                        <div class="app-hub-stat__value">{{ summary.in_pipeline }}</div>
                    </div>
                </el-col>
                <el-col :xs="12" :sm="12" :md="6">
                    <div class="app-hub-stat app-hub-stat--ok">
                        <div class="app-hub-stat__label">已转化</div>
                        <div class="app-hub-stat__value">{{ summary.won }}</div>
                    </div>
                </el-col>
                <el-col :xs="12" :sm="12" :md="6">
                    <div class="app-hub-stat app-hub-stat--muted">
                        <div class="app-hub-stat__label">已流失</div>
                        <div class="app-hub-stat__value">{{ summary.lost }}</div>
                    </div>
                </el-col>
            </el-row>

            <el-table
                :data="list"
                border
                stripe
                size="small"
                v-loading="loading"
                class="app-hub-table customer-data-page__table-centered"
            >
                <el-table-column label="日期" width="85" align="center" header-align="center" show-overflow-tooltip>
                    <template slot-scope="{ row }">{{ formatLeadDate(row) }}</template>
                </el-table-column>
                <el-table-column
                    prop="company_name"
                    label="公司名称"
                    width="250"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column
                    prop="credit_code"
                    label="统一信用代码"
                    width="160"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column
                    prop="contact_name"
                    label="微信号"
                    width="110"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column
                    prop="phone"
                    label="法人手机号"
                    width="105"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column label="线索所属" width="85" align="center" header-align="center" show-overflow-tooltip>
                    <template slot-scope="{ row }">
                        <span v-if="row.lead_belonging_name">{{ row.lead_belonging_name }}</span>
                        <span v-else class="customer-data-page__dash">—</span>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="source"
                    label="线索来源"
                    width="75"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column
                    prop="intent_product"
                    label="意向业务"
                    width="75"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                />
                <el-table-column
                    prop="next_follow_up_at"
                    label="下次跟进时间"
                    width="140"
                    align="center"
                    header-align="center"
                    show-overflow-tooltip
                >
                    <template slot-scope="{ row }">
                        {{ row.next_follow_up_at ? row.next_follow_up_at.substring(0, 10) : '' }}
                    </template>
                </el-table-column>
                <el-table-column label="跟踪详情" width="75" align="center" header-align="center">
                    <template slot-scope="{ row }">
                        <el-tooltip
                            v-if="row.remark"
                            placement="top"
                            effect="dark"
                            popper-class="customer-data-page__track-detail-tooltip"
                        >
                            <div slot="content" class="customer-data-page__track-detail-tooltip__body">{{ row.remark }}</div>
                            <span
                                class="customer-data-page__track-detail-trigger"
                                :class="{
                                    'customer-data-page__track-detail-trigger--due':
                                        row.remark && isFollowUpDue(row)
                                }"
                            >
                                {{ row.stage_label }}
                            </span>
                        </el-tooltip>
                        <span v-else class="customer-data-page__dash">{{ row.stage_label }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="100" align="center" header-align="center" fixed="right">
                    <template slot-scope="{ row }">
                        <el-button type="text" size="mini" @click="openEdit(row)">编辑</el-button>
                        <el-button
                            v-if="row.converted_order_id"
                            type="text"
                            size="mini"
                            @click="goOrder(row.converted_order_id)"
                        >
                            打开订单
                        </el-button>
                        <el-button type="text" size="mini" class="customer-data-page__danger" @click="confirmDelete(row)">
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <div v-if="listMeta.total > 0" class="customer-data-page__pager app-hub-pager app-pagination-antd-wrap">
                <el-pagination
                    background
                    layout="total, prev, pager, next, sizes"
                    popper-class="app-pagination-antd-sizes-popper"
                    :page-sizes="[10, 20, 50, 100]"
                    :current-page="listPage"
                    :page-size="listPerPage"
                    :total="listMeta.total"
                    @current-change="handleLeadPageChange"
                    @size-change="handleLeadPageSizeChange"
                />
            </div>
        </el-card>

        <el-dialog
            :title="dialogTitle"
            :visible.sync="dialogVisible"
            width="620px"
            @closed="onDialogClosed"
        >
            <el-form v-if="form" ref="formRef" :model="form" label-width="120px" size="small">
                <el-form-item label="公司名称" required>
                    <el-input v-model.trim="form.company_name" maxlength="200" show-word-limit />
                </el-form-item>
                <el-form-item label="统一信用代码" required>
                    <el-input
                        :value="form.credit_code"
                        maxlength="18"
                        show-word-limit
                        placeholder="18 位，数字与大写字母"
                        @input="onCreditCodeInput"
                    />
                </el-form-item>
                <el-form-item label="微信号">
                    <el-input v-model.trim="form.contact_name" maxlength="80" />
                </el-form-item>
                <el-form-item label="法人手机号">
                    <el-input
                        :value="form.phone"
                        maxlength="11"
                        show-word-limit
                        placeholder="11 位手机号，1 开头"
                        inputmode="numeric"
                        @input="onLegalPhoneInput"
                    />
                </el-form-item>
                <el-form-item label="来源">
                    <el-select v-model="form.source" filterable allow-create default-first-option style="width: 100%;">
                        <el-option label="抖音" value="抖音" />
                        <el-option label="转介绍" value="转介绍" />
                        <el-option label="其他渠道" value="其他渠道" />
                    </el-select>
                </el-form-item>
                <el-form-item label="意向业务">
                    <el-select
                        v-model="form.intent_product"
                        clearable
                        filterable
                        allow-create
                        default-first-option
                        placeholder="请选择意向业务"
                        style="width: 100%;"
                    >
                        <el-option
                            v-for="opt in intentProductOptions"
                            :key="opt"
                            :label="opt"
                            :value="opt"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="跟进阶段" required>
                    <el-select v-model="form.stage" style="width: 100%;">
                        <el-option
                            v-for="opt in stageOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="下次跟进时间">
                    <el-date-picker
                        v-model="form.next_follow_up_at"
                        type="datetime"
                        format="yyyy-MM-dd HH:mm"
                        value-format="yyyy-MM-dd HH:mm"
                        placeholder="选择时间（精确到分）"
                        style="width: 100%;"
                    />
                </el-form-item>
                <el-form-item label="跟踪详情">
                    <el-input
                        v-model="form.remark"
                        type="textarea"
                        :autosize="{ minRows: 3, maxRows: 13 }"
                        maxlength="2000"
                        show-word-limit
                        resize="vertical"
                        placeholder="支持换行输入"
                        class="customer-data-page__remark-input"
                    />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'CustomerData',
    data() {
        return {
            loading: false,
            saving: false,
            keyword: '',
            channelFilter: null,
            leadCreatorOptions: [],
            stageFilter: '',
            listPage: 1,
            listPerPage: 10,
            listMeta: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0
            },
            list: [],
            summary: {
                total: 0,
                in_pipeline: 0,
                won: 0,
                lost: 0
            },
            dialogVisible: false,
            editingId: null,
            form: null,
            stageOptions: [
                { value: 'lead', label: '新线索' },
                { value: 'follow', label: '跟进中' },
                { value: 'quote', label: '已报价' },
                { value: 'won', label: '已转化' },
                { value: 'lost', label: '已流失' }
            ],
            intentProductOptions: ['执照注销', '税务申报', '公司注册', '银行注销'],
            followUpReminderTimer: null
        };
    },
    computed: {
        dialogTitle() {
            return this.editingId ? '编辑线索' : '新增线索';
        }
    },
    created() {
        this.fetchList();
        this._followUpReminderLastSoundSlot = null;
    },
    mounted() {
        var _this = this;
        this.followUpReminderTimer = setInterval(function () {
            _this.tickFollowUpReminders();
        }, 1000);
    },
    beforeDestroy() {
        if (this.followUpReminderTimer) {
            clearInterval(this.followUpReminderTimer);
            this.followUpReminderTimer = null;
        }
        this._followUpReminderLastSoundSlot = null;
    },
    methods: {
        formatLeadDate(row) {
            var s = row && row.created_at ? String(row.created_at) : '';
            if (!s) {
                return '—';
            }
            if (s.length >= 10) {
                return s.slice(0, 10);
            }
            return s;
        },
        parseLeadDateTime(s) {
            if (!s) {
                return null;
            }
            var str = String(s).trim();
            if (!str) {
                return null;
            }
            var norm = str.indexOf('T') === -1 ? str.replace(' ', 'T') : str;
            var d = new Date(norm);
            if (isNaN(d.getTime())) {
                return null;
            }
            return d;
        },
        isFollowUpDue(row) {
            if (!row || !row.next_follow_up_at) {
                return false;
            }
            var t = this.parseLeadDateTime(row.next_follow_up_at);
            if (!t) {
                return false;
            }
            return t.getTime() <= Date.now();
        },
        tickFollowUpReminders() {
            var list = this.list;
            var fiveMin = 5 * 60 * 1000;
            var soundMs = 5000;
            var maxSlots = 10;
            if (!list || !list.length) {
                this._followUpReminderLastSoundSlot = null;
                return;
            }
            var now = Date.now();
            var activeSlot = null;
            var r;
            var row;
            var t0;
            var t0ms;
            var i;
            var slotStart;
            var slotEnd;
            for (r = 0; r < list.length; r++) {
                row = list[r];
                if (!row || !row.next_follow_up_at) {
                    continue;
                }
                if (!this.isFollowUpDue(row)) {
                    continue;
                }
                t0 = this.parseLeadDateTime(row.next_follow_up_at);
                if (!t0) {
                    continue;
                }
                t0ms = t0.getTime();
                for (i = 0; i < maxSlots; i++) {
                    slotStart = t0ms + i * fiveMin;
                    slotEnd = slotStart + soundMs;
                    if (now >= slotStart && now < slotEnd) {
                        activeSlot = String(slotStart);
                        break;
                    }
                }
                if (activeSlot) {
                    break;
                }
            }
            if (!activeSlot) {
                this._followUpReminderLastSoundSlot = null;
                return;
            }
            if (activeSlot !== this._followUpReminderLastSoundSlot) {
                this._followUpReminderLastSoundSlot = activeSlot;
                var slotStartNum = parseInt(activeSlot, 10);
                var remain = Math.max(0, Math.min(soundMs, slotStartNum + soundMs - now));
                if (remain > 0) {
                    this.playFollowUpReminderSound(remain);
                }
            }
        },
        playFollowUpReminderSound(durationMs) {
            var Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx || durationMs <= 0) {
                return;
            }
            var ctx;
            try {
                ctx = new Ctx();
            } catch (e) {
                return;
            }
            var stopAt = Date.now() + durationMs;
            var scheduleBeep = function () {
                if (Date.now() >= stopAt) {
                    try {
                        ctx.close();
                    } catch (e2) {}
                    return;
                }
                var osc = ctx.createOscillator();
                var gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = 880;
                osc.connect(gain);
                gain.connect(ctx.destination);
                gain.gain.setValueAtTime(0.1, ctx.currentTime);
                osc.start();
                setTimeout(function () {
                    try {
                        osc.stop();
                    } catch (e3) {}
                }, 160);
                setTimeout(scheduleBeep, 380);
            };
            scheduleBeep();
        },
        normalizeCreditCodeInput(raw) {
            var s = String(raw || '')
                .replace(/[^0-9A-Za-z]/g, '')
                .toUpperCase();
            return s.length > 18 ? s.slice(0, 18) : s;
        },
        normalizeLegalPhoneInput(raw) {
            return String(raw || '')
                .replace(/\D/g, '')
                .slice(0, 11);
        },
        normalizeNextFollowForPicker(val) {
            if (!val) {
                return null;
            }
            var s = String(val).trim();
            if (s.length >= 16) {
                return s.slice(0, 16) + ':00';
            }
            return s;
        },
        onCreditCodeInput(val) {
            this.form.credit_code = this.normalizeCreditCodeInput(val);
        },
        onLegalPhoneInput(val) {
            this.form.phone = this.normalizeLegalPhoneInput(val);
        },
        emptyForm() {
            return {
                company_name: '',
                credit_code: '',
                contact_name: '',
                phone: '',
                source: '抖音',
                intent_product: '',
                stage: 'lead',
                owner_name: '',
                last_follow_up_at: null,
                next_follow_up_at: null,
                converted_order_id: null,
                remark: ''
            };
        },
        resetLeadToolbarFilters() {
            this.keyword = '';
            this.channelFilter = null;
            this.stageFilter = '';
            this.listPage = 1;
            this.fetchList();
        },
        onChannelFilterChange() {
            this.listPage = 1;
            this.fetchList();
        },
        onLeadStageFilterChange() {
            this.listPage = 1;
            this.fetchList();
        },
        searchLeadsFromToolbar() {
            this.listPage = 1;
            this.fetchList();
        },
        handleLeadPageChange(p) {
            this.listPage = p;
            this.fetchList();
        },
        handleLeadPageSizeChange(size) {
            this.listPerPage = size;
            this.listPage = 1;
            this.fetchList();
        },
        fetchList() {
            var _this = this;
            this.loading = true;

            window.axios
                .get('/customer-leads', {
                    params: {
                        page: this.listPage,
                        per_page: this.listPerPage,
                        keyword: this.keyword || undefined,
                        stage: this.stageFilter || undefined,
                        created_by_user_id:
                            _this.channelFilter !== null && _this.channelFilter !== undefined && _this.channelFilter !== ''
                                ? _this.channelFilter
                                : undefined
                    }
                })
                .then(function (response) {
                    _this.list = response.data.data || [];
                    _this.leadCreatorOptions = response.data.lead_creator_options || [];
                    var m = response.data.meta;
                    if (m) {
                        _this.listMeta = {
                            current_page: Number(m.current_page || 1),
                            last_page: Number(m.last_page || 1),
                            per_page: Number(m.per_page || _this.listPerPage),
                            total: Number(m.total || 0)
                        };
                        _this.listPage = _this.listMeta.current_page;
                        _this.listPerPage = _this.listMeta.per_page;
                    } else {
                        _this.listMeta = { current_page: 1, last_page: 1, per_page: _this.listPerPage, total: 0 };
                    }
                    var s = response.data.summary || {};
                    _this.summary = {
                        total: Number(s.total || 0),
                        in_pipeline: Number(s.in_pipeline || 0),
                        won: Number(s.won || 0),
                        lost: Number(s.lost || 0)
                    };
                })
                .catch(function () {
                    _this.$message.error('线索列表加载失败');
                })
                .finally(function () {
                    _this.loading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.form = this.emptyForm();
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.editingId = row.id;
            this.form = {
                company_name: row.company_name || '',
                credit_code: this.normalizeCreditCodeInput(row.credit_code || ''),
                contact_name: row.contact_name || '',
                phone: this.normalizeLegalPhoneInput(row.phone || ''),
                source: row.source || '抖音',
                intent_product: row.intent_product || '',
                stage: row.stage || 'lead',
                owner_name: row.owner_name || '',
                last_follow_up_at: row.last_follow_up_at || null,
                next_follow_up_at: this.normalizeNextFollowForPicker(row.next_follow_up_at),
                converted_order_id: row.converted_order_id || null,
                remark: row.remark || ''
            };
            this.dialogVisible = true;
        },
        onDialogClosed() {
            this.form = null;
            this.editingId = null;
        },
        submitForm() {
            var _this = this;

            if (!this.form.company_name) {
                this.$message.warning('请填写公司名称');
                return;
            }
            if (!this.form.credit_code || this.form.credit_code.length !== 18) {
                this.$message.warning('请填写 18 位统一社会信用代码');
                return;
            }
            if (this.form.phone && !/^1\d{10}$/.test(this.form.phone)) {
                this.$message.warning('法人手机号须为 1 开头的 11 位数字');
                return;
            }

            this.saving = true;
            var payload = Object.assign({}, this.form);
            if (payload.next_follow_up_at && String(payload.next_follow_up_at).length >= 16) {
                payload.next_follow_up_at = String(payload.next_follow_up_at).slice(0, 16) + ':00';
            }
            if (typeof payload.remark === 'string') {
                payload.remark = payload.remark.replace(/^\s+|\s+$/g, '');
            }
            if (payload.converted_order_id === '' || payload.converted_order_id === undefined || payload.converted_order_id === null) {
                payload.converted_order_id = null;
            } else {
                var oid = parseInt(String(payload.converted_order_id).trim(), 10);
                payload.converted_order_id = isNaN(oid) ? null : oid;
            }

            var req;
            if (this.editingId) {
                req = window.axios.put('/customer-leads/' + this.editingId, payload);
            } else {
                req = window.axios.post('/customer-leads', payload);
            }

            req
                .then(function () {
                    _this.$message.success('保存成功');
                    _this.dialogVisible = false;
                    _this.fetchList();
                })
                .catch(function (error) {
                    var message =
                        error && error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : '保存失败';
                    _this.$message.error(message);
                })
                .finally(function () {
                    _this.saving = false;
                });
        },
        confirmDelete(row) {
            var _this = this;

            this.$confirm('确定删除该条线索？', '提示', {
                type: 'warning'
            })
                .then(function () {
                    return window.axios.delete('/customer-leads/' + row.id);
                })
                .then(function () {
                    _this.$message.success('已删除');
                    _this.fetchList();
                })
                .catch(function (err) {
                    if (err === 'cancel') {
                        return;
                    }

                    if (err && err.response) {
                        _this.$message.error('删除失败');
                    }
                });
        },
        goOrder(orderId) {
            this.$router.push({ name: 'order-detail', params: { id: String(orderId) } });
        }
    }
};
</script>

<style scoped>
.customer-data-page__header {
    width: 100%;
}

.customer-data-page__header-leading {
    flex-shrink: 0;
}

.customer-data-page__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    flex-shrink: 0;
    justify-content: flex-end;
    min-width: 0;
}

.customer-data-page__search {
    width: 280px;
    max-width: 100%;
}

.customer-data-page__channel-filter {
    width: 150px;
}

.customer-data-page__stage-filter {
    width: 130px;
}

.customer-data-page__table-centered /deep/ .el-table__header .cell,
.customer-data-page__table-centered /deep/ .el-table__body .cell {
    text-align: center;
}

.customer-data-page__dash {
    color: #c0c4cc;
}

.customer-data-page__danger {
    color: #f56c6c;
}

.customer-data-page__remark-input /deep/ textarea {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
}

.customer-data-page__track-detail-trigger {
    cursor: pointer;
    color: inherit;
    user-select: none;
}

@keyframes customer-data-followup-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.35;
    }
}

.customer-data-page__track-detail-trigger--due {
    color: #f56c6c !important;
    animation: customer-data-followup-blink 1s ease-in-out infinite;
}
</style>

<style>
/* el-tooltip 挂载在 body，需非 scoped */
.customer-data-page__track-detail-tooltip {
    max-width: min(420px, 85vw);
}
.customer-data-page__track-detail-tooltip .customer-data-page__track-detail-tooltip__body {
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
    line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/views/CustomerData.vue